// ---------------------------------------------------------------------------
// The top bar.
//
// It carries the information a cashier needs to glance at without leaving the
// till: which page they are on, the date and time in Beirut, who is signed in
// and with what role, whether a shift is open, the current exchange rate,
// whether the connection is up, and the way out.
// ---------------------------------------------------------------------------

import { useCallback, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Clock, LogOut, Menu, User } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useI18n } from '../../i18n/I18nProvider';
import { useClock } from '../../hooks/useClock';
import { useExchangeRate } from '../../api/system.api';
import { useCurrentShift } from '../../api/sales.api';
import { formatLongDate, formatTime, toDecimal } from '../../utils/format';
import { NAV_ITEMS } from '../../routes/navigation';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { NotificationBell } from '../common/NotificationBell';
import { ConnectionStatus } from '../common/ConnectionStatus';
import { HeaderPopover } from '../common/HeaderPopover';
import { Spinner } from '../common/Spinner';
import { YaghiLogo } from '../common/YaghiLogo';

export function Header({ onOpenMobileMenu }: { onOpenMobileMenu: () => void }) {
  const { t, locale, format } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const now = useClock();

  const user = useAuthStore((state) => state.user);
  const role = useAuthStore((state) => state.role);
  const logout = useAuthStore((state) => state.logout);

  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  /// The account button. The menu is placed from its own rectangle.
  const accountRef = useRef<HTMLButtonElement>(null);
  const accountLabelId = useId();

  // Stable, so opening the menu does not re-register and close itself.
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const { data: rate } = useExchangeRate();
  const { data: shiftState } = useCurrentShift();
  const shift = shiftState?.shift ?? null;

  const currentNavItem = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path));
  const pageTitle = currentNavItem ? t.nav[currentNavItem.labelKey] : t.common.appName;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setSigningOut(false);
      setMenuOpen(false);
    }
  };

  const roleName = role ? (locale === 'ar' ? role.nameAr : role.nameEn) : '';

  return (
    // `min-w-0` and `overflow-hidden`: the bar compacts instead of pushing the
    // document sideways. Every chip inside is `shrink-0` and hidden below its
    // own breakpoint, so what goes first is the least useful thing, in order.
    <header className="z-20 flex h-16 min-w-0 shrink-0 items-center gap-2 overflow-hidden border-b border-line bg-surface px-3 shadow-sm sm:gap-3 sm:px-5">
      {/* --- mobile menu button ------------------------------------------- */}
      <button
        type="button"
        onClick={onOpenMobileMenu}
        className="btn-ghost -ms-1 rounded-control p-2 lg:hidden"
        aria-label={t.nav.expand}
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </button>

      {/* The logo, for the narrow screens where the sidebar is hidden - so the
          shop's identity is present on a phone as well as at the till.
          Decorative here, because the page title already names where you are. */}
      <YaghiLogo height={30} className="shrink-0 lg:hidden" decorative />

      {/* --- page title and date ------------------------------------------ */}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-semibold text-ink sm:text-lg">{pageTitle}</h1>
        <p className="hidden truncate text-xs text-ink-muted sm:block">
          {formatLongDate(now, locale)}
          <span className="numeric mx-1.5">·</span>
          <span className="numeric">{formatTime(now, locale)}</span>
        </p>
      </div>

      {/* --- status chips -------------------------------------------------- */}
      {/* Every one of these repeats inside the user menu, so hiding them on a
          narrow screen loses nothing. `till:` rather than `md:`: on a 1093 px
          laptop the exchange rate is worth less than the product grid. */}
      <div className="hidden shrink-0 items-center gap-2 till:flex">
        {/* Exchange rate */}
        <div className="shrink-0 whitespace-nowrap rounded-control border border-line bg-surface-soft px-3 py-1.5 text-xs">
          <span className="text-ink-muted">{t.header.exchangeRate}</span>
          <span className="numeric ms-2 font-semibold text-ink">
            {rate ? toDecimal(rate.usdToLbp).toDecimalPlaces(0).toFixed(0) : '—'}
          </span>
          {rate ? <span className="ms-1 text-ink-muted">LBP</span> : null}
        </div>

        {/* Shift indicator. Text and icon, never colour alone. */}
        <div
          className={clsx(
            'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-control border px-3 py-1.5 text-xs font-medium',
            shift
              ? 'border-butcher-green/30 bg-butcher-green/10 text-butcher-green'
              : 'border-line bg-surface-soft text-ink-muted',
          )}
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          <span>
            {shift ? format(t.header.shiftNumber, { number: shift.shiftNumber }) : t.header.shiftClosed}
          </span>
        </div>
      </div>

      {/* THE connectivity badge - there is exactly one, and this is it.
          The header used to carry a second chip of its own driven by
          `navigator.onLine`, which reports whether the machine has a network
          interface up. On a shop's Wi-Fi that is very often true while
          nothing can reach the server, so the two badges regularly disagreed
          and the wrong one was the reassuring one. This one asks the server. */}
      <ConnectionStatus />

      {/* Always visible, including on a phone: a low-stock or overdue warning
          is exactly the thing somebody needs to see while they are away from
          the desk. */}
      <NotificationBell />

      <div className="hidden shrink-0 till:block">
        <LanguageSwitcher />
      </div>

      {/* --- user menu ----------------------------------------------------- */}
      <div className="shrink-0">
        <button
          ref={accountRef}
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="flex shrink-0 items-center gap-2 rounded-control border border-line bg-surface px-2 py-1.5 hover:bg-surface-sunken"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={t.header.openMenu}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-butcher-green text-xs font-semibold text-white">
            {user?.fullName?.trim().charAt(0).toUpperCase() ?? '?'}
          </span>
          <span className="hidden text-start leading-tight sm:block">
            <span className="block max-w-[9rem] truncate text-xs font-semibold text-ink">
              {user?.fullName ?? ''}
            </span>
            <span className="block max-w-[9rem] truncate text-[11px] text-ink-muted">{roleName}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-ink-muted" aria-hidden="true" />
        </button>

        <HeaderPopover
          id="account"
          open={menuOpen}
          onClose={closeMenu}
          anchorRef={accountRef}
          role="menu"
          labelledBy={accountLabelId}
          width="w-64"
          className="p-1.5"
        >
            <div className="rule-gold px-3 pb-2.5 pt-2">
              <p id={accountLabelId} className="text-sm font-semibold text-ink">
                {user?.fullName}
              </p>
              <p className="text-xs text-ink-muted">@{user?.username}</p>
              <p className="mt-1.5 inline-flex items-center gap-1.5 rounded bg-surface-sunken px-2 py-0.5 text-[11px] font-medium text-ink-muted">
                <User className="h-3 w-3" aria-hidden="true" />
                {roleName}
              </p>
            </div>

            {/* The shift line only. The connection badge used to be repeated
                here for small screens, but it is now a button that opens its
                own panel - a second copy would be a second dropdown inside a
                dropdown, and two things claiming to be THE connectivity badge
                is exactly what this work set out to remove. The badge itself
                stays in the header bar at every width; below `sm` it shows its
                icon and its count without the word. */}
            <div className="space-y-2 px-3 py-2 text-xs text-ink-muted till:hidden">
              <p>{shift ? t.header.shiftOpen : t.header.shiftClosed}</p>
            </div>

            <div className="px-3 py-2 till:hidden">
              <LanguageSwitcher />
            </div>

            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              disabled={signingOut}
              className="mt-1 flex w-full items-center gap-2.5 rounded-control px-3 py-2.5 text-sm font-medium text-burgundy-red hover:bg-burgundy-red/10 disabled:opacity-60"
            >
              {signingOut ? (
                <Spinner size="sm" />
              ) : (
                <LogOut className="h-4 w-4" aria-hidden="true" />
              )}
              {signingOut ? t.common.signingOut : t.common.signOut}
            </button>
        </HeaderPopover>
      </div>
    </header>
  );
}
